/**
 * Cockpit visual editor — the PAGE MANAGER + the declared bindings/actions panel.
 *
 * Two small surfaces:
 *
 *  1. PageTabs — a tab strip for the cockpit's pages (main first, then extras).
 *     Switch the page being edited; add / rename / reorder / delete extra pages.
 *     Respects the schema cap (COCKPIT_SPEC_MAX_PAGES extra pages).
 *
 *  2. DeclaredRefsPanel — declare the binding / action ids the spec's nodes may
 *     reference. A node prop wired to an UNDECLARED ref fails `parseCockpitSpec`
 *     (BINDING_REF_NOT_DECLARED / ACTION_REF_NOT_DECLARED), so the editor lets
 *     the author declare ids up front, then pick them in the props form's slot
 *     selectors. (The concrete binding source — the toolSlug/table/params — is
 *     authored in the Bindings JSON tab / the data-fetch manifest; here we only
 *     declare the ID the spec references.)
 */

import { useState } from 'react';
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  MAIN_PAGE_INDEX,
  isValidRefId,
  type EditorPageRef,
} from './cockpit-editor-model';

// ─── Page tabs ───────────────────────────────────────────────────────────────

export interface CockpitPageTabsProps {
  pages: EditorPageRef[];
  activeIndex: number;
  canAddPage: boolean;
  disabled?: boolean;
  onSelect: (index: number) => void;
  onAdd: () => void;
  onRename: (index: number, title: string) => void;
  onReorder: (index: number, direction: 'left' | 'right') => void;
  onDelete: (index: number) => void;
}

export function CockpitPageTabs({
  pages,
  activeIndex,
  canAddPage,
  disabled,
  onSelect,
  onAdd,
  onRename,
  onReorder,
  onDelete,
}: CockpitPageTabsProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draftTitle, setDraftTitle] = useState('');

  const startRename = (page: EditorPageRef) => {
    setEditingIndex(page.index);
    setDraftTitle(page.title);
  };
  const commitRename = () => {
    if (editingIndex !== null && draftTitle.trim()) {
      onRename(editingIndex, draftTitle.trim());
    }
    setEditingIndex(null);
  };

  return (
    <div
      className="flex items-center gap-1 overflow-x-auto border-b border-border/40 px-2 py-1"
      role="tablist"
      aria-label="Cockpit pages"
      data-testid="cockpit-page-tabs"
    >
      {pages.map((page, pos) => {
        const isActive = page.index === activeIndex;
        const isMain = page.index === MAIN_PAGE_INDEX;
        const isEditing = editingIndex === page.index;
        return (
          <div
            key={page.id}
            className={cn(
              'group flex shrink-0 items-center gap-0.5 rounded-md border px-1.5 py-0.5 transition-colors',
              isActive
                ? 'border-primary/40 bg-primary/10'
                : 'border-transparent hover:bg-accent/60',
            )}
          >
            {isEditing ? (
              <>
                <Input
                  autoFocus
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename();
                    if (e.key === 'Escape') setEditingIndex(null);
                  }}
                  maxLength={80}
                  aria-label="Page title"
                  className="h-6 w-28 text-xs"
                />
                <Button
                  type="button" variant="ghost" size="icon" className="h-5 w-5"
                  onClick={commitRename} aria-label="Save page name"
                >
                  <Check className="h-3 w-3" />
                </Button>
                <Button
                  type="button" variant="ghost" size="icon" className="h-5 w-5"
                  onClick={() => setEditingIndex(null)} aria-label="Cancel rename"
                >
                  <X className="h-3 w-3" />
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-page-id={page.id}
                  onClick={() => onSelect(page.index)}
                  className={cn(
                    'whitespace-nowrap px-1.5 py-0.5 text-xs font-medium',
                    isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {page.title}
                </button>
                {!disabled ? (
                  <div className="flex items-center opacity-0 transition-opacity group-hover:opacity-100">
                    {!isMain ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button" variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground"
                            disabled={pos <= 1}
                            onClick={() => onReorder(page.index, 'left')}
                            aria-label="Move page left"
                          >
                            <ChevronLeft className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Move left</TooltipContent>
                      </Tooltip>
                    ) : null}
                    {!isMain ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button" variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground"
                            disabled={pos >= pages.length - 1}
                            onClick={() => onReorder(page.index, 'right')}
                            aria-label="Move page right"
                          >
                            <ChevronRight className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Move right</TooltipContent>
                      </Tooltip>
                    ) : null}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button" variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground"
                          onClick={() => startRename(page)} aria-label="Rename page"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Rename page</TooltipContent>
                    </Tooltip>
                    {!isMain ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button" variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive"
                            onClick={() => onDelete(page.index)} aria-label="Delete page"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete page</TooltipContent>
                      </Tooltip>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        );
      })}
      {!disabled ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button" variant="ghost" size="sm"
              className="h-6 shrink-0 px-2 text-xs"
              disabled={!canAddPage}
              onClick={onAdd}
              data-testid="cockpit-add-page"
            >
              <Plus className="mr-1 h-3 w-3" />
              Page
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {canAddPage ? 'Add a page' : 'Maximum pages reached'}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}

// ─── Declared bindings / actions panel ──────────────────────────────────────

export interface DeclaredRefsPanelProps {
  bindingIds: string[];
  actionIds: string[];
  disabled?: boolean;
  onAddBinding: (id: string) => void;
  onRemoveBinding: (id: string) => void;
  onAddAction: (id: string) => void;
  onRemoveAction: (id: string) => void;
}

export function DeclaredRefsPanel({
  bindingIds,
  actionIds,
  disabled,
  onAddBinding,
  onRemoveBinding,
  onAddAction,
  onRemoveAction,
}: DeclaredRefsPanelProps) {
  return (
    <div className="flex flex-col gap-4 p-3">
      <RefList
        title="Data bindings"
        hint="IDs your components can read from. Wire the actual source in the Bindings JSON manifest."
        ids={bindingIds}
        disabled={disabled}
        onAdd={onAddBinding}
        onRemove={onRemoveBinding}
        testid="cockpit-binding"
      />
      <RefList
        title="Actions"
        hint="Approval-gated action IDs your buttons can trigger."
        ids={actionIds}
        disabled={disabled}
        onAdd={onAddAction}
        onRemove={onRemoveAction}
        testid="cockpit-action"
      />
    </div>
  );
}

function RefList({
  title,
  hint,
  ids,
  disabled,
  onAdd,
  onRemove,
  testid,
}: {
  title: string;
  hint: string;
  ids: string[];
  disabled?: boolean;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  testid: string;
}) {
  const [draft, setDraft] = useState('');
  const trimmed = draft.trim();
  const valid = trimmed.length > 0 && isValidRefId(trimmed) && !ids.includes(trimmed);
  const add = () => {
    if (valid) {
      onAdd(trimmed);
      setDraft('');
    }
  };
  return (
    <div>
      <h4 className="text-xs font-semibold">{title}</h4>
      <p className="mb-1.5 text-[10px] text-muted-foreground">{hint}</p>
      {!disabled ? (
        <div className="mb-2 flex gap-1.5">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            placeholder={`${title.slice(0, -1)} id…`}
            aria-label={`New ${title} id`}
            className="h-7 font-mono text-[11px]"
            data-testid={`${testid}-input`}
          />
          <Button
            type="button" size="sm" variant="outline"
            className="h-7 px-2 text-[11px]"
            disabled={!valid}
            onClick={add}
            data-testid={`${testid}-add`}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      ) : null}
      {trimmed.length > 0 && !valid && !ids.includes(trimmed) ? (
        <p className="mb-1.5 text-[10px] text-destructive">
          IDs use letters, numbers, “.”, “_”, “-”, “:” (≤120 chars).
        </p>
      ) : null}
      {ids.length === 0 ? (
        <p className="text-[11px] italic text-muted-foreground">None declared.</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {ids.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-mono text-[11px]"
            >
              {id}
              {!disabled ? (
                <button
                  type="button"
                  onClick={() => onRemove(id)}
                  aria-label={`Remove ${id}`}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
